(() => {
  const context = document.getElementById('ReefShippingContext');
  if (!context || customElements.get('reef-shipping-message')) return;
  let config;
  try { config = JSON.parse(context.textContent); } catch (_) { return; }
  const choiceKey = 'rr-shipping-country-choice';
  const cacheKey = 'rr-shipping-detected-country';
  const validCountry = (value) => typeof value === 'string' && /^[A-Z]{2}$/.test(value);
  const readChoice = () => {
    try {
      const country = localStorage.getItem(choiceKey);
      return validCountry(country) ? country : null;
    } catch (_) { return null; }
  };
  // An explicit, accepted market link must override a saved delivery destination.
  const requestedCountry = new URL(window.location.href).searchParams.get('country')?.toUpperCase();
  const explicitCountry = validCountry(requestedCountry) && requestedCountry === config.country ? requestedCountry : null;
  let chosenCountry = explicitCountry || readChoice();
  if (explicitCountry) {
    try { localStorage.setItem(choiceKey, explicitCountry); } catch (_) {}
  }
  let activeCountry = chosenCountry || config.country;
  const update = () => document.querySelectorAll('reef-shipping-message, reef-delivery-estimate').forEach((message) => message.render());
  customElements.define('reef-shipping-message', class extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      const carrier = config.carriers[activeCountry];
      const hideUnknown = this.hasAttribute('data-hide-unknown');
      this.textContent = carrier ? config.prefix + carrier : hideUnknown ? '' : config.fallback;
      this.dataset.shippingCountry = activeCountry;
      const row = this.closest('[data-shipping-carrier-row]');
      if (row) row.hidden = !carrier;
    }
  });
  const transitWindow = country => config.transit?.[country] || config.transit?.default || [10, 15];
  const addBusinessDays = (date, days) => {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
    while (days > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) days--;
    }
    return result;
  };
  customElements.define('reef-delivery-estimate', class extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      this.dataset.shippingCountry = activeCountry;
      this.replaceChildren();
      const destination = document.createElement('p');
      destination.className = 'rr-information__destination';
      let name = activeCountry;
      try { name = new Intl.DisplayNames(['en'], {type: 'region'}).of(activeCountry); } catch (_) {}
      if (this.hasAttribute('data-compact')) {
        if (['PF', 'RU', 'CN'].includes(activeCountry)) {
          const note = document.createElement('p');
          note.textContent = this.dataset.unavailable;
          this.append(note);
          return;
        }
        const countryNames = {GB: 'the UK', US: 'the USA', AU: 'Australia', IE: 'Ireland', CA: 'Canada', NZ: 'New Zealand'};
        const heading = document.createElement('strong');
        heading.textContent = this.dataset.heading.replace('[country]', countryNames[activeCountry] || name);
        const carrier = config.carriers[activeCountry];
        const estimate = document.createElement('p');
        estimate.className = 'rr-lyon-delivery__arrival';
        if (carrier?.includes(' or ')) estimate.className += ' rr-lyon-delivery__arrival--multiple';
        const compactCarrier = carrier?.replace(/, | or /g, '/');
        if (compactCarrier) {
          const carrierHighlight = document.createElement('b');
          carrierHighlight.className = 'rr-lyon-delivery__courier';
          carrierHighlight.textContent = compactCarrier;
          estimate.append(config.prefix, carrierHighlight);
        }
        this.append(heading);
        if (compactCarrier) this.append(estimate);
        return;
      }
      destination.textContent = `Delivering to ${name}`;
      const destinationRow = document.createElement('div');
      destinationRow.className = 'rr-information__destination-row';
      destinationRow.append(destination);
      const selector = document.getElementById('FooterCountryForm')?.closest('localization-form');
      if (selector) {
        const change = document.createElement('p');
        change.className = 'rr-information__location-prompt';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rr-information__location-button';
        button.dataset.shippingLocationChange = '';
        button.textContent = 'Change location';
        button.setAttribute('aria-haspopup', 'dialog');
        const panel = selector.querySelector('dialog');
        if (panel?.id) button.setAttribute('aria-controls', panel.id);
        button.addEventListener('click', async () => {
          await customElements.whenDefined('localization-form');
          selector.openSelector(undefined, button, activeCountry);
        });
        if (!this.hasAttribute('data-aligned-details')) change.append(document.createTextNode('Shipping somewhere else? '));
        change.append(button);
        destinationRow.append(change);
      }
      this.append(destinationRow);
      if (['PF', 'RU', 'CN'].includes(activeCountry)) {
        const note = document.createElement('p');
        note.textContent = 'We currently do not ship to this destination. Please see our delivery guide.';
        this.append(note);
        return;
      }
      if (this.hasAttribute('data-aligned-details')) {
        const labels = config.accordion;
        if (!labels) return;
        const facts = document.createElement('dl');
        facts.className = 'rr-approved-delivery__facts';
        const row = (label, value, emphasize = false, secondary = '') => {
          const item = document.createElement('div');
          const term = document.createElement('dt');
          term.textContent = label;
          const detail = document.createElement('dd');
          const main = document.createElement(emphasize ? 'strong' : 'span');
          main.textContent = value;
          detail.append(main);
          if (secondary) {
            const sub = document.createElement('span');
            sub.className = 'rr-approved-delivery__carrier';
            sub.textContent = secondary;
            detail.append(sub);
          }
          item.append(term, detail);
          facts.append(item);
        };
        const carrier = config.carriers[activeCountry];
        row(labels.delivery, labels.freeTracked, true, carrier ? config.prefix + carrier : '');
        row(labels.dispatch, labels.dispatchCopy);
        row(labels.returns, labels.returnsCopy);
        this.append(facts);
        return;
      }
      const [min, max] = transitWindow(activeCountry);
      const format = date => new Intl.DateTimeFormat('en', {day: 'numeric', month: 'short'}).format(date);
      const now = new Date();
      const table = document.createElement('table');
      table.className = 'rr-information__delivery-table';
      const caption = document.createElement('caption');
      caption.className = 'visually-hidden';
      caption.textContent = `Delivery estimate for ${name}`;
      const head = document.createElement('thead');
      const headRow = document.createElement('tr');
      ['Delivery method', 'Estimated arrival'].forEach(label => {
        const cell = document.createElement('th'); cell.scope = 'col'; cell.textContent = label; headRow.append(cell);
      });
      head.append(headRow);
      const body = document.createElement('tbody');
      const row = document.createElement('tr');
      const method = document.createElement('td');
      method.textContent = 'Free tracked shipping';
      const carrier = config.carriers[activeCountry];
      if (carrier) {
        const carrierText = document.createElement('span');
        carrierText.className = 'rr-information__inline-carrier';
        carrierText.textContent = ` · ${config.prefix}${carrier}`;
        method.append(carrierText);
      }
      const arrival = document.createElement('td');
      arrival.textContent = `${format(addBusinessDays(now, min + (config.preparation?.min ?? 1)))} – ${format(addBusinessDays(now, max + (config.preparation?.max ?? 2)))}`;
      row.append(method, arrival); body.append(row); table.append(caption, head, body); this.append(table);
    }
  });
  document.addEventListener('reef:shipping-country', (event) => {
    if (!validCountry(event.detail?.country)) return;
    chosenCountry = activeCountry = event.detail.country;
    update();
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/localization')) return;
    const country = new FormData(form).get('country_code');
    if (!validCountry(country)) return;
    chosenCountry = activeCountry = country;
    try { localStorage.setItem(choiceKey, country); } catch (_) {}
    update();
  }, true);
  const detectCountry = async () => {
    if (chosenCountry || !document.querySelector('reef-shipping-message, reef-delivery-estimate')) return;
    try {
      let cached;
      try { cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null'); } catch (_) {}
      let country = cached?.country;
      if (!validCountry(country) || Date.now() - cached.time > 1800000) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 4000);
        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(root + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=false', {
            credentials: 'same-origin', signal: controller.signal
          });
          if (!response.ok) return;
          const data = await response.json();
          country = data.detected_values?.country?.handle;
          if (!validCountry(country)) return;
          try { sessionStorage.setItem(cacheKey, JSON.stringify({ country, time: Date.now() })); } catch (_) {}
        } finally { clearTimeout(timer); }
      }
      if (!chosenCountry && !readChoice()) {
        activeCountry = country;
        update();
      }
    } catch (_) {}
  };
  detectCountry();
})();

